import React, { useState } from 'react';
import { useStudy } from '../context/StudyContext';
import './FileUpload.css';

function FileUpload({ onSuccess, onCancel }) {
  const { addStudySet } = useStudy();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFile(e.dataTransfer.files[0]);
      setError(null);
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      setError(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file to upload');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const fileContent = await file.text();
      const studySet = JSON.parse(fileContent);
      
      // Add the study set using context
      addStudySet(studySet);
      onSuccess();
    } catch (err) {
      if (err.message.includes('JSON')) {
        setError('Invalid JSON file format');
      } else {
        setError(err.message || 'Failed to process file');
      }
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="file-upload">
      <div className="upload-card">
        <h2>Upload Study Set</h2>
        <p className="upload-description">
          Upload a JSON file containing your study set questions and answers
        </p>

        <div
          className={`drop-zone ${dragActive ? 'active' : ''}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <div className="drop-zone-content">
            <div className="upload-icon">📁</div>
            {file ? (
              <>
                <p className="file-name">{file.name}</p>
                <p className="file-size">{(file.size / 1024).toFixed(2)} KB</p>
              </>
            ) : (
              <>
                <p className="drop-text">Drag & drop your JSON file here</p>
                <p className="or-text">or</p>
              </>
            )}
            <label className="browse-button">
              Browse Files
              <input
                type="file"
                accept=".json"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
            </label>
          </div>
        </div>

        {error && (
          <div className="error-message">
            <span className="error-icon">⚠️</span> {error}
          </div>
        )}

        <div className="format-info">
          <h4>Expected JSON Format:</h4>
          <pre className="format-example">
{`{
  "id": "unique-id",
  "title": "Study Set Title",
  "description": "Description",
  "difficulty": "easy|medium|hard",
  "questions": [
    {
      "id": "q1",
      "question": "Question text?",
      "answer": "Correct answer",
      "hint": "Optional hint",
      "choices": ["Option 1", "Option 2"],
      "explanation": "Why this is correct"
    }
  ]
}`}
          </pre>
        </div>

        <div className="upload-actions">
          <button
            className="upload-button"
            onClick={handleUpload}
            disabled={!file || uploading}
          >
            {uploading ? 'Processing...' : 'Upload Study Set'}
          </button>
          <button className="cancel-button" onClick={onCancel} disabled={uploading}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default FileUpload;
